import { routeDefinitions, type RouteDefinition } from './route-config';

export interface RouteConstant<TName extends string = string> {
  readonly name: TName;
  readonly path: string;
  readonly params?: Record<string, string | number>;
  readonly query?: Record<string, string | (string | null)[] | null | undefined>;
}

type ToRouteConstant<T> =
  T extends RouteDefinition<unknown>
    ? T extends { children: infer C }
      ? C extends Record<string, unknown>
        ? { readonly name: T['name']; readonly path: T['path'] } & { [K in keyof C]: ToRouteConstant<C[K]> }
        : { readonly name: T['name']; readonly path: T['path'] }
      : { readonly name: T['name']; readonly path: T['path'] }
    : T extends Record<string, unknown>
      ? { [K in keyof T]: ToRouteConstant<T[K]> }
      : never;

export const Routes = convertToConstants(routeDefinitions) as unknown as ToRouteConstant<typeof routeDefinitions>;

export function childRoute<T extends { name: string; path: string }, K extends string>(
  parent: T,
  childKey: K,
): RouteConstant {
  const child = (parent as Record<string, unknown>)[childKey];

  if (!child || typeof child !== 'object' || !('name' in child) || !('path' in child)) {
    throw new Error(
      `Child route '${childKey}' does not exist on parent route '${parent.name}'. ` +
        `Available properties: ${Object.keys(parent)
          .filter(k => k !== 'name' && k !== 'path')
          .join(', ')}`,
    );
  }

  return child as RouteConstant;
}

function convertToConstants<T extends Record<string, unknown>>(definitions: T): ToRouteConstant<T> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(definitions)) {
    if (value && typeof value === 'object') {
      if ('name' in value && 'path' in value) {
        const routeConstant: Record<string, unknown> = {
          name: value.name,
          path: value.path,
        };

        if ('children' in value && value.children && typeof value.children === 'object') {
          const childrenConstants = convertToConstants(value.children as Record<string, unknown>);
          Object.assign(routeConstant, childrenConstants);
        }

        result[key] = routeConstant;
      } else {
        result[key] = convertToConstants(value as Record<string, unknown>);
      }
    }
  }

  return result as ToRouteConstant<T>;
}

type ExtractRoutes<T> = T extends { name: string; path: string }
  ? T
  : T extends object
    ? { [K in keyof T]: ExtractRoutes<T[K]> }[keyof T]
    : never;
export type RouteConstantType = ExtractRoutes<typeof Routes>;

export type ExtractRouteName<T> = T extends { name: infer N } ? N : never;

export type RouteName = ExtractRouteName<RouteConstantType>;

type ExtractParams<T> =
  T extends RouteDefinition<infer P>
    ? P extends never
      ? T extends { children: infer C }
        ? ExtractParams<C>
        : Record<never, never>
      : T['name'] extends string
        ? T extends { children: infer C }
          ? { [K in T['name']]: P } & ExtractParams<C>
          : { [K in T['name']]: P }
        : Record<never, never>
    : T extends Record<string, unknown>
      ? { [K in keyof T]: ExtractParams<T[K]> }[keyof T]
      : Record<never, never>;

type UnionToIntersection<U> = (U extends unknown ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

export type RouteParams = UnionToIntersection<ExtractParams<typeof routeDefinitions>>;

export type RoutesWithParams = {
  [K in RouteName]: K extends keyof RouteParams ? K : never;
}[RouteName];

export type RoutesWithoutParams = Exclude<RouteName, RoutesWithParams>;

export type TypedRouteLocation<T extends RouteName> = T extends RoutesWithParams
  ? {
      name: T;
      params: RouteParams[T];
      query?: Record<string, string | (string | null)[] | null | undefined>;
      hash?: string;
    }
  : {
      name: T;
      params?: never;
      query?: Record<string, string | (string | null)[] | null | undefined>;
      hash?: string;
    };

export type RouteRequiresParams<T extends RouteConstant> = ExtractRouteName<T> extends RoutesWithParams ? true : false;
